import React, { useCallback, useMemo, useState } from 'react';

import pluralize from 'pluralize';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import { Brand } from '@luxuryescapes/lib-regions/lib/regions';

import PermissionedComponent from '~/components/Common/PermissionedComponent';

import { brandsByValue } from '~/consts/brands';
import { LUX_PLUS } from '~/consts/membership';

import { AVAILABILITY, LIST_VISIBILITY, LUX_PLUS_SCHEDULE, ONLINE_PURCHASE } from '../../../../consts/schedule';

import ScheduleBulkUpdateContainer from './ScheduleBulkUpdateContainer';
import ScheduleTable from './ScheduleTable';
import { useScheduleSection } from './useScheduleSection';
import { useUndoableDelete } from './useUndoableDelete';
import { buildScheduleDisplayMap } from './utils';

interface Props {
  brand?: Brand;
  affectedBrands?: Array<Brand>;
  offerId: string;
  shouldMigrateSchedule: boolean;
  isExternallyDeleting?: boolean;
  refreshToken?: number;
  scheduleTypes?: Array<string>;
}

export default function Schedules({
  brand = 'luxuryescapes',
  affectedBrands,
  offerId,
  shouldMigrateSchedule,
  isExternallyDeleting = false,
  refreshToken,
  scheduleTypes,
}: Props) {
  const {
    schedules,
    isLoading,
    error,
    updateErrorMessages,
    invalidateSchedules,
    onSaveFinished,
    clearErrors,
    onError,
    onUpdateErrors,
    onDelete,
    isRefetching,
    showSchedules,
  } = useScheduleSection({ offerId, shouldMigrateSchedule, refreshToken });

  const [isDeleteAllOpen, setIsDeleteAllOpen] = useState(false);
  const [isDeleteLuxPlusOpen, setIsDeleteLuxPlusOpen] = useState(false);

  const brandList = useMemo(() => affectedBrands ?? [brand], [affectedBrands, brand]);
  const brandTitle = brandList.map((b) => brandsByValue[b].title).join(' / ');

  // When multiple brands share one table (e.g. Agent Hub + CVFR), deduplicate rows:
  // - If all brands have the same schedule for a region → show one row, label = region only
  // - If only some brands have it, or dates differ → show each separately with brand appended
  // warningById carries an inline warning message for out-of-sync rows.
  const { scheduleFilterFn, scheduleGetLabel, warningById } = useMemo(() => {
    if (!affectedBrands) {
      return {
        scheduleFilterFn: undefined,
        scheduleGetLabel: undefined,
        warningById: new Map<number, string>(),
      };
    }

    const { labelById, warningById } = buildScheduleDisplayMap(schedules, brandList, brand);

    return {
      scheduleFilterFn: (_s: App.Schedule, type: string) => _s.type === type && labelById.has(_s.id),
      scheduleGetLabel: (s: App.Schedule) => labelById.get(s.id) ?? s.region,
      warningById,
    };
  }, [affectedBrands, schedules, brandList, brand]);

  const scheduleTableTypes = useMemo(() => scheduleTypes ?? [LIST_VISIBILITY, LUX_PLUS_SCHEDULE], [scheduleTypes]);
  const hasLuxPlusScheduleType = scheduleTableTypes.includes(LUX_PLUS_SCHEDULE);
  const hasPurchaseSchedules =
    schedules.some((s) => s.type === AVAILABILITY) && schedules.some((s) => s.type === ONLINE_PURCHASE);
  const deletableScheduleCount = schedules.filter(
    (s) => scheduleTableTypes.includes(s.type) && brandList.some((b) => s.brand === b),
  ).length;
  const luxPlusScheduleCount = hasLuxPlusScheduleType
    ? schedules.filter((s) => s.type === LUX_PLUS_SCHEDULE && brandList.some((b) => s.brand === b)).length
    : 0;

  const getDeleteAllIdList = useCallback(
    () =>
      schedules
        .filter((s) => scheduleTableTypes.includes(s.type) && brandList.includes(s.brand as Brand))
        .map((s) => s.id),
    [schedules, scheduleTableTypes, brandList],
  );

  const getDeleteLuxPlusIdList = useCallback(
    () =>
      schedules.filter((s) => s.type === LUX_PLUS_SCHEDULE && brandList.includes(s.brand as Brand)).map((s) => s.id),
    [schedules, brandList],
  );

  const { confirm: executeDeleteAll, isDeleting: isDeletingAll } = useUndoableDelete({
    offerId,
    label: `All ${deletableScheduleCount} ${pluralize('schedule', deletableScheduleCount)} for ${brandTitle}`,
    getIdList: getDeleteAllIdList,
    onSuccess: invalidateSchedules,
    onError,
  });

  const { confirm: executeDeleteLuxPlus, isDeleting: isDeletingLuxPlus } = useUndoableDelete({
    offerId,
    label: `All ${luxPlusScheduleCount} LuxPlus ${pluralize('schedule', luxPlusScheduleCount)} for ${brandTitle}`,
    getIdList: getDeleteLuxPlusIdList,
    onSuccess: invalidateSchedules,
    onError,
  });

  const confirmDeleteAll = useCallback(() => {
    if (shouldMigrateSchedule) {
      return;
    }
    setIsDeleteAllOpen(false);
    executeDeleteAll();
  }, [shouldMigrateSchedule, executeDeleteAll]);

  const confirmDeleteLuxPlus = useCallback(() => {
    if (shouldMigrateSchedule) {
      return;
    }
    setIsDeleteLuxPlusOpen(false);
    executeDeleteLuxPlus();
  }, [shouldMigrateSchedule, executeDeleteLuxPlus]);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Box>
          <Typography variant="h6">
            {brandTitle}
            {showSchedules && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                ({deletableScheduleCount} {pluralize('schedule', deletableScheduleCount)})
              </Typography>
            )}
          </Typography>
          {window.configs?.DEFAULT_TIMEZONE && (
            <Typography variant="body2" color="text.secondary">
              All times in {window.configs.DEFAULT_TIMEZONE}
            </Typography>
          )}
          <Alert severity="info" variant="outlined" sx={{ mt: 1, py: 0 }}>
            <Typography variant="body2">When adding schedules, start from the top and work down.</Typography>
          </Alert>
        </Box>
        {showSchedules && (
          <Stack direction="row" gap={1}>
            {hasLuxPlusScheduleType && (
              <ScheduleBulkUpdateContainer
                brand={brand}
                offerId={offerId}
                schedules={schedules}
                type={LUX_PLUS_SCHEDULE}
                onOpen={clearErrors}
                onSaveFinished={onSaveFinished}
                shouldMigrateSchedule={shouldMigrateSchedule}
                onError={invalidateSchedules}
                title={`Bulk Update ${LUX_PLUS.PROGRAM_NAME}`}
              />
            )}
            <ScheduleBulkUpdateContainer
              brand={brand}
              offerId={offerId}
              schedules={schedules}
              type={LIST_VISIBILITY}
              onOpen={clearErrors}
              onSaveFinished={onSaveFinished}
              shouldMigrateSchedule={shouldMigrateSchedule}
              onError={invalidateSchedules}
              disabled={!hasPurchaseSchedules}
            />
            {hasLuxPlusScheduleType && luxPlusScheduleCount > 0 && (
              <PermissionedComponent>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => setIsDeleteLuxPlusOpen(true)}
                  disabled={shouldMigrateSchedule}
                >
                  Delete Lux+
                </Button>
              </PermissionedComponent>
            )}
            {deletableScheduleCount > 0 && (
              <PermissionedComponent>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => setIsDeleteAllOpen(true)}
                  disabled={shouldMigrateSchedule}
                >
                  Delete All
                </Button>
              </PermissionedComponent>
            )}
          </Stack>
        )}
      </Stack>
      <Paper variant="outlined" sx={{ position: 'relative', p: { xs: 2, sm: 3, md: '24px 40px' }, overflow: 'hidden' }}>
        {isRefetching && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0 }} />}
        {error && <Alert severity="error">{error.message}</Alert>}
        {updateErrorMessages && (
          <Alert severity="error">
            Error updating schedules:
            <ul>
              {updateErrorMessages.map((message, i) => (
                <li key={i}>{message}</li>
              ))}
            </ul>
          </Alert>
        )}
        {isLoading && (
          <Box py={4} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        )}
        {showSchedules && (
          <ScheduleTable
            brand={brand}
            offerId={offerId}
            schedules={schedules}
            types={scheduleTableTypes}
            onDelete={onDelete}
            onSaveFinished={onSaveFinished}
            onUpdateErrors={onUpdateErrors}
            shouldMigrateSchedule={shouldMigrateSchedule}
            isDeletingAll={isDeletingAll || isExternallyDeleting}
            isDeletingLuxPlus={isDeletingLuxPlus}
            filterFn={scheduleFilterFn}
            getLabel={scheduleGetLabel}
            getRowWarning={affectedBrands ? (s) => warningById.get(s.id) : undefined}
            columnLabel={affectedBrands ? 'Country / Brand' : 'Country'}
          />
        )}
      </Paper>
      <Dialog open={isDeleteAllOpen} onClose={() => setIsDeleteAllOpen(false)}>
        <DialogTitle>Delete all schedules?</DialogTitle>
        <DialogContent>
          <Typography>
            This will delete all {deletableScheduleCount} {pluralize('schedule', deletableScheduleCount)} for{' '}
            {brandTitle}. You will have 5 seconds to undo.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            To avoid accidental removals, delete schedules from the bottom and work up.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteAllOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDeleteAll} autoFocus>
            Delete All
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={isDeleteLuxPlusOpen} onClose={() => setIsDeleteLuxPlusOpen(false)}>
        <DialogTitle>Delete all LuxPlus schedules?</DialogTitle>
        <DialogContent>
          <Typography>
            This will delete all {luxPlusScheduleCount} LuxPlus {pluralize('schedule', luxPlusScheduleCount)} for{' '}
            {brandTitle}. You will have 5 seconds to undo.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            To avoid accidental removals, delete schedules from the bottom and work up.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteLuxPlusOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDeleteLuxPlus} autoFocus>
            Delete Lux+
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
